import { useMutation } from '@tanstack/react-query';
import type { SendTestEmailRequest, SendTestEmailResponse } from '@metron/shared';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useApiClient } from './useApiClient.ts';

/**
 * Send (or preview, with `dryRun`) a Cognito email template as a test email.
 * See POST /test-emails (MET-3729).
 *
 * `defaultRecipient` is the signed-in user's email (metron-auth reads it from
 * the ID token's `email` claim), shown so the dialog says where the email will
 * go. It is display only: the dialog doesn't send it, and the API looks the
 * caller's email up from Cognito instead.
 */
export function useSendTestEmail() {
  const apiClient = useApiClient();
  const { user } = useAuth();

  const mutation = useMutation({
    mutationFn: (request: SendTestEmailRequest): Promise<SendTestEmailResponse> =>
      apiClient.post<SendTestEmailResponse>('test-emails', request),
  });

  return {
    sendTestEmail: mutation.mutateAsync,
    isPending: mutation.isPending,
    defaultRecipient: user?.email ?? '',
  };
}
